import { AddressValidations } from '@utility/constants/validater';
import { z } from 'zod';

/* The provider's shipping calculator: a parcel to a zip, priced per carrier. */

export const SPEED_OPTIONS = ['Standard', 'Expedited', 'Priority'].map(
  speed => ({ value: speed, label: speed }),
);

const MAX_POUNDS = 70;
const MAX_SIDE = 108;

const side = (name: string) =>
  z
    .number({
      required_error: `${name} is required`,
      invalid_type_error: `${name} is required`,
    })
    .gt(0, `${name} is required`)
    .max(MAX_SIDE, `${name} cannot be more than ${MAX_SIDE} in.`);

export const rateCalculatorSchema = z
  .object({
    shipTo: z
      .string()
      .trim()
      .min(1, 'Ship To Zip is required')
      .min(4, 'Zip is too short')
      .max(10, 'Zip is too long')
      .regex(AddressValidations.zipcode, 'Invalid format'),
    shipFrom: z.string().optional(),
    speed: z.string(),
    pounds: z.number().min(0).nullable(),
    ounces: z.number().min(0).nullable(),
    length: side('Length'),
    width: side('Width'),
    height: side('Height'),
  })
  .superRefine((values, ctx) => {
    const pounds = values.pounds ?? 0;
    const ounces = values.ounces ?? 0;
    if (pounds <= 0 && ounces <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['pounds'],
        message: 'Weight is required',
      });
    }
    if (pounds > MAX_POUNDS || (pounds >= MAX_POUNDS && ounces > 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['pounds'],
        message: `Weight cannot be more than ${MAX_POUNDS} pounds`,
      });
    }
  });

export type RateCalculatorValues = {
  shipFrom?: string;
  shipTo: string;
  speed: string;
  pounds: number | null;
  ounces: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
};

export const EMPTY_PARCEL: RateCalculatorValues = {
  shipTo: '',
  speed: 'Standard',
  pounds: null,
  ounces: null,
  length: null,
  width: null,
  height: null,
};

/** 16 oz and more roll over into pounds, keeping the remainder. */
export function carryOunces(pounds: number | null, ounces: number | null) {
  if (!ounces || ounces < 16) return null;
  return {
    pounds: (pounds ?? 0) + Math.floor(ounces / 16),
    ounces: ounces % 16,
  };
}

export function toRatePayload(values: RateCalculatorValues) {
  return {
    pounds: values.pounds,
    ounces: values.ounces,
    length: values.length,
    width: values.width,
    height: values.height,
    order_speed: values.speed,
    destination: { zip: values.shipTo.trim() },
  };
}

/* One quote of `POST /fulfillment-provider/:id/rate`. */
export type CarrierRate = {
  provider: { key: string };
  service: { key: string };
  rate: number;
  currency?: string;
  error?: unknown;
};

/** The quotes that came back priced, cheapest first. */
export function pricedRates(rates?: CarrierRate[]) {
  return (rates ?? [])
    .filter(rate => !rate.error)
    .sort((a, b) => a.rate - b.rate);
}
